import { useState } from 'react';

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'How quickly can you buy my apartments?',
      answer: 'We can close in as little as 7-14 days once you accept our offer. The timeline depends primarily on how quickly the title company can complete their title search and prepare closing documents. We have cash ready and can move as fast as you need.'
    },
    {
      question: 'Do you really buy apartments with cash?',
      answer: 'Yes, absolutely. We are legitimate apartment buyers with proven financial resources. We can provide proof of funds and references from title companies where we\'ve closed previous transactions. Every purchase is backed by cash, eliminating financing contingencies.'
    },
    {
      question: 'What types of apartments do you buy?',
      answer: 'We buy all types of multi-family properties: duplexes, triplexes, fourplexes, small apartment buildings (5-12 units), medium complexes (12-30 units), and large apartment buildings (30+ units). We also purchase mixed-use properties with retail and residential components.'
    },
    {
      question: 'Will you buy apartments that need repairs?',
      answer: 'Yes, we buy apartments in any condition. Whether your property needs minor cosmetic updates or major structural repairs, we make offers based on the property\'s potential, not its current condition. You don\'t need to make any repairs before selling.'
    },
    {
      question: 'What if my apartments have tenants?',
      answer: 'We buy apartments with tenants in place. In fact, occupied apartments with existing leases can be more valuable because they generate immediate income. We take over all tenant relationships, leases, and security deposits. You don\'t need to evict anyone or coordinate tenant relocations.'
    },
    {
      question: 'How do you determine your offer price?',
      answer: 'We analyze multiple factors: current rental income, operating expenses, property condition, comparable sales, location, occupancy rates, and market conditions. We provide a detailed explanation of our valuation methodology so you understand exactly how we arrived at our offer.'
    },
    {
      question: 'Are there any fees or commissions?',
      answer: 'No. We don\'t charge commissions, and we cover most closing costs. Our offer is what you receive. Unlike traditional sales where you pay 6% in real estate commissions plus 2-3% in closing costs, selling to us means you keep more of your equity.'
    },
    {
      question: 'Why do you use title companies?',
      answer: 'Title companies protect both parties in real estate transactions. They conduct title searches, resolve liens, hold funds in escrow, prepare legal documents, and ensure proper transfer of ownership. Using title companies is the professional, secure way to handle apartment sales.'
    },
    {
      question: 'Can you provide references or proof of previous purchases?',
      answer: 'Absolutely. We can provide references from title companies, previous sellers, and our real estate attorney. We have a documented track record of 150+ apartment building purchases totaling over $85 million in transaction value. We\'re happy to verify our credentials.'
    },
    {
      question: 'What if I have an existing mortgage or liens on the property?',
      answer: 'Not a problem. The title company will identify all liens, mortgages, and encumbrances during their title search. These are paid off from the sale proceeds at closing. We work with the title company to ensure all obligations are satisfied and you receive clear title transfer.'
    },
    {
      question: 'Do I need to provide rent rolls and financial statements?',
      answer: 'Yes, we\'ll need to review your rent rolls, operating expenses, and financial statements to make an accurate offer. This information helps us understand the property\'s income potential and operating costs. Don\'t worry if your records aren\'t perfect—we can work with what you have.'
    },
    {
      question: 'What makes you different from other apartment buyers?',
      answer: 'Our proven track record, financial strength, and professional approach. We\'ve completed 150+ apartment purchases, we close through title companies for your protection, we provide proof of funds, and we can offer references. We\'re not wholesalers or middlemen—we\'re the actual buyers.'
    },
    {
      question: 'Can you close on my timeline?',
      answer: 'Yes, we work around your schedule. If you need to close quickly, we can do it in 7-14 days. If you need more time to coordinate your next move or handle other matters, we can accommodate longer timelines. We\'re flexible and work on your terms.'
    },
    {
      question: 'What happens to tenant security deposits?',
      answer: 'Security deposits are transferred to us at closing and credited against the purchase price. We take over the obligation to return deposits to tenants when they move out. The title company handles all the accounting to ensure proper transfer of these funds.'
    },
    {
      question: 'Is there any obligation when I contact you?',
      answer: 'None whatsoever. Contacting us is completely free with no obligation. We\'ll evaluate your property, make you a fair offer, and you can take all the time you need to decide. There\'s no pressure—we want you to make the decision that\'s right for your situation.'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            FAQ
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions About Selling Apartments
          </h2>
          <p className="text-xl text-gray-600">
            Common questions from property owners who want to sell my apartments to professional buyers
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <span className="text-lg font-bold text-gray-900 pr-8">{faq.question}</span>
                <i className={`ri-arrow-${openIndex === index ? 'up' : 'down'}-s-line text-2xl text-teal-600 flex-shrink-0`}></i>
              </button>
              {openIndex === index && (
                <div className="px-8 pb-6">
                  <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-lg text-gray-700 mb-6">
            Still have questions about how to sell my apartments fast?
          </p>
          <a 
            href="tel://+19152065814"
            className="inline-block px-10 py-5 bg-teal-600 text-white rounded-lg font-bold text-lg hover:bg-teal-700 transition-all transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-fill mr-2"></i>
            Call Us at (915) 206-5814
          </a>
        </div>
      </div>
    </section>
  );
}